import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  ChevronRight,
  FitnessCenter,
  Timer,
  LocalFireDepartment,
  MilitaryTechOutlined
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { MY_PLAY_RESULTS, MY_PLAY_WORKOUT_SUMMARY } from 'api/graphql/play';
import { formatDuration } from 'components/play/workoutEstimate';
import routes from 'common/routes';

// Métrica compacta (icono + valor + etiqueta) de la parrilla de progreso.
function Metric({ icon, value, label }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.overlay'
      }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'gold.main', mb: 0.5 }}>
        {icon}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }} noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

Metric.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired
};

/**
 * OTRIX Play — card "Tu progreso" del dashboard, apuntada a RENDIMIENTOS (no ranking).
 * Resume entrenamientos completados, tiempo total, racha y resultados; el CTA lleva al
 * historial de rendimientos.
 */
function PlayPerformanceSummaryCard() {
  const { t } = useTranslation(['play']);
  const navigate = useNavigate();

  const { data: resData } = useQuery(MY_PLAY_RESULTS, { variables: {}, fetchPolicy: 'cache-and-network' });
  const { data: sumData } = useQuery(MY_PLAY_WORKOUT_SUMMARY, { fetchPolicy: 'cache-and-network' });

  const results = useMemo(() => resData?.myPlayResults || [], [resData]);
  const summary = sumData?.myPlayWorkoutSummary || null;

  const completedTotal = summary?.completed_total || 0;
  const totalSeconds = summary?.total_seconds || 0;
  const streakDays = summary?.current_streak_days || 0;
  const isEmpty = completedTotal === 0 && results.length === 0;

  return (
    <PremiumCard>
      <Stack spacing={2}>
        {/* Sin noWrap el título y el CTA se partían en dos líneas cada uno al apretarse. */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ minWidth: 0 }}>
            {t('perfSummary.title', { ns: 'play' })}
          </Typography>
          <Button
            size="small"
            endIcon={<ChevronRight />}
            onClick={() => navigate(routes.playPerformance.path)}
            sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700, minWidth: 0, flex: 'none', whiteSpace: 'nowrap' }}>
            {t('perfSummary.viewPerformance', { ns: 'play' })}
          </Button>
        </Stack>

        {isEmpty ? (
          <Stack spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('perfSummary.empty', { ns: 'play' })}
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate(routes.playRoutine.path)}
              sx={{ color: 'gold.main', fontWeight: 700, textTransform: 'none' }}>
              {t('perfSummary.emptyCta', { ns: 'play' })}
            </Button>
          </Stack>
        ) : (
          // La parrilla era `1fr 1fr` FIJA: a 320 la segunda columna se salía de la card
          // ("Tiempo tota…", "Resultados" cortados). En xs va en una sola columna.
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5
            }}>
            <Metric
              icon={<FitnessCenter sx={{ fontSize: 18 }} />}
              value={completedTotal}
              label={t('perfSummary.sessions', { ns: 'play' })}
            />
            <Metric
              icon={<Timer sx={{ fontSize: 18 }} />}
              value={formatDuration(totalSeconds)}
              label={t('perfSummary.totalTime', { ns: 'play' })}
            />
            <Metric
              icon={<LocalFireDepartment sx={{ fontSize: 18 }} />}
              value={t('perfSummary.days', { ns: 'play', count: streakDays })}
              label={t('perfSummary.streak', { ns: 'play' })}
            />
            <Metric
              icon={<MilitaryTechOutlined sx={{ fontSize: 18 }} />}
              value={results.length}
              label={t('perfSummary.results', { ns: 'play' })}
            />
          </Box>
        )}
      </Stack>
    </PremiumCard>
  );
}

export default PlayPerformanceSummaryCard;
